import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import endpoints from "../../api/endpoints";
import apiInstance from "../../api/instance";
import useGetApi from "../../hooks/use-get-api";
import usePostApi2 from "../../hooks/use-post-api";
import parseNumber from "../../utils/parse-number";
import { getUserRoles } from "../../redux/reducers/appSlice";
import { addAccountSchema } from "../../utils/validation-schema";
import { IAddAccountPayload, IAddAccountResponse } from "../../types";
import { showErrorToast } from "src/utils/toast";
import { RootState } from "../../redux/store";

const MIN_USERNAME_LENGTH = 4;

export default function AddAccount() {
  const dispatch = useDispatch<any>();
  const roles = useSelector<any>(({ app }) => app.roles);
  const navigate = useNavigate();
  const { postData } = usePostApi2<IAddAccountResponse, IAddAccountPayload>(endpoints.ACCOUNT.ADD);
  const userData = useSelector((state: RootState) => state.user.userData);
  const loggedInUserId = userData?.user_id ?? "";
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const usernameRequestIdRef = useRef(0);

  const { data: memberData } = useGetApi<any>(endpoints.member.getMember(loggedInUserId), !!loggedInUserId);

  const { values, touched, errors, setFieldValue, handleChange, handleSubmit, handleBlur } = useFormik({
    initialValues: {
      user_name: "",
      display_name: "",
      password: "",
      confirm_password: "",
      city: "",
      phone: "",
      credit_reference: 0,
      member_max_exposure_amt: 0,
      role_id: "",
      role_name: "",
      commission_settings: {
        up_line: 0,
        down_line: 0,
        our: 0,
      },
      partnership: {
        up_line: 0,
        down_line: 0,
        our: 0,
      },
      transaction_password: "",
      member_min_bet: 0,
      member_max_bet_amt: 0,
      bet_delay: 0,
    },
    onSubmit: (formValues) => {
      handle.addAccount(formValues);
    },
    validationSchema: addAccountSchema,
  });

  useEffect(() => {
    dispatch(getUserRoles());
  }, [dispatch]);

  const validateUsernameAvailability = useCallback(async (username: string): Promise<boolean> => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername || trimmedUsername.length < MIN_USERNAME_LENGTH) {
      setUsernameStatus("idle");
      return false;
    }

    const requestId = ++usernameRequestIdRef.current;
    setUsernameStatus("checking");

    try {
      const response = await apiInstance.get(endpoints.member.getUsernameValid(trimmedUsername));
      const isValid = Boolean(response?.data?.data?.is_valid ?? response?.data?.is_valid);
      if (requestId !== usernameRequestIdRef.current) return false;

      setUsernameStatus(isValid ? "valid" : "invalid");
      return isValid;
    } catch (error: any) {
      const isValidFromError = error?.response?.data?.data?.is_valid ?? error?.response?.data?.is_valid;
      if (requestId === usernameRequestIdRef.current) {
        setUsernameStatus(isValidFromError === false ? "invalid" : "idle");
      }
      return false;
    }
  }, []);

  useEffect(() => {
    const trimmedUsername = values.user_name.trim();
    if (!trimmedUsername || trimmedUsername.length < MIN_USERNAME_LENGTH || errors.user_name) {
      setUsernameStatus("idle");
      return;
    }

    const timer = setTimeout(() => {
      validateUsernameAvailability(trimmedUsername);
    }, 350);

    return () => clearTimeout(timer);
  }, [values.user_name, errors.user_name, validateUsernameAvailability]);

  const handle = {
    addAccount: async (formData: IAddAccountPayload) => {
      const isUsernameValid = await validateUsernameAvailability(formData.user_name);
      if (!isUsernameValid) return;

      const newPayload: IAddAccountPayload = {
        user_name: formData.user_name,
        display_name: formData.display_name,
        password: formData.password,
        // confirm_password: formData.confirm_password,
        // role_id: formData.role_id,
        role_name: formData.role_name,
        credit_reference: parseNumber({ type: "float", value: formData.credit_reference }),
        opening_balance: 0,
        // commission_settings: {
        //   up_line: parseNumber({ type: "float", value: formData.commission_settings.up_line }),
        //   down_line: parseNumber({ type: "float", value: formData.commission_settings.down_line }),
        //   our: parseNumber({ type: "float", value: formData.commission_settings.our }),
        // },
        // commission: parseNumber({ type: "float", value: formData.commission_settings.down_line }),
        commission: 1,
        partnership: parseNumber({ type: "float", value: formData.partnership.down_line }),
        // partnership: {
        //   up_line: parseNumber({ type: "float", value: formData.partnership.up_line }),
        //   down_line: parseNumber({ type: "float", value: formData.partnership.down_line }),
        //   our: parseNumber({ type: "float", value: formData.partnership.our }),
        // },
        transaction_password: formData.transaction_password,
      };

      if (process.env.NODE_ENV === "development") {
        newPayload.base_url = "https://betmaverick.in";
      }

      if (formData?.role_name && formData?.role_name?.toLowerCase() === "user") {
        newPayload.member_min_bet = parseNumber({ type: "float", value: formData.member_min_bet });
        newPayload.member_max_bet_amt = parseNumber({ type: "float", value: formData.member_max_bet_amt });

        newPayload.bet_delay = formData.bet_delay ?? 0;
        newPayload.member_max_exposure_amt = parseNumber({
          type: "float",
          value: formData.member_max_exposure_amt,
        });
      }

      postData(newPayload)
        .then(() => {
          navigate(`/active-users/${loggedInUserId}`);
        })
        .catch((error) => {
          showErrorToast(error.status_message ?? "Something Went Wrong");
        });
    },
  };
  
  const getUserOptions = () => {
    try {
      if (!Array.isArray(roles) || !userData?.role_name) return [];
  
      const currentRole = userData.role_name.toLowerCase();
  
      const hierarchy: Record<string, string[]> = {
        "super-admin": ["ultra-admin","pro-admin","master", "agent", "user"],
        "ultra-admin": ["pro-admin","master", "agent", "user"],
        "pro-admin": ["master", "agent", "user"],
        master: ["agent", "user"],
        agent: ["user"],
      };
  
      const allowedRoles = hierarchy[currentRole] || [];
  
      // 🔥 Build result in correct order from hierarchy
      const orderedRoles = allowedRoles
        .map((roleName) =>
          roles.find(
            (r) => r.name?.toLowerCase()?.trim() === roleName
          )
        )
        .filter(Boolean) // remove undefined if not found
        .map((r: any) => ({
          ...r,
          value: r.role_id,
          label: r.name,
        }));
  
      orderedRoles.unshift({ label: "Select Account Type", value: "" });
  
      return orderedRoles;
    } catch (error) {
      return [];
    }
  };

  const isUser = values.role_name && values.role_name?.toLowerCase() === "user";
  const hasUserNameSchemaError = Boolean(touched.user_name && errors.user_name);
  const hasAvailableCheck = values.user_name.trim().length >= MIN_USERNAME_LENGTH && !errors.user_name;
  const userNameInputClass = `form-control${hasUserNameSchemaError || (hasAvailableCheck && usernameStatus === "invalid") ? " is-invalid" : ""}${hasAvailableCheck && usernameStatus === "valid" ? " is-valid" : ""}`;

  return (
    <div className="page-content">
      <div>
        <div className="add-account">
          <h2 className="m-b-20">Add Account</h2>
          <form method="post" onSubmit={handleSubmit}>
            <div className="row">
              {/* Personal Detail */}
              <div className="col-6">
                <h4 className="mb-4 col-12">Personal Detail</h4>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="user_name">Client Name:</label>
                      <input
                        type="text"
                        placeholder="Client Name"
                        name="user_name"
                        className={userNameInputClass}
                        value={values.user_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.user_name && errors.user_name && (
                        <small className="error">{errors.user_name}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="password">User Password:</label>
                      <input
                        type="password"
                        placeholder="User Password"
                        name="password"
                        className={`form-control${touched.password && errors.password ? " is-invalid" : ""}`}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.password && errors.password && (
                        <small className="error">{errors.password}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="confirm_password">Retype Password:</label>
                      <input
                        type="password"
                        placeholder="Retype Password"
                        name="confirm_password"
                        className={`form-control${touched.confirm_password && errors.confirm_password ? " is-invalid" : ""}`}
                        value={values.confirm_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.confirm_password && errors.confirm_password && (
                        <small className="error">{errors.confirm_password}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="display_name">Full Name:</label>
                      <input
                        type="text"
                        placeholder="Full Name"
                        name="display_name"
                        className={`form-control${touched.display_name && errors.display_name ? " is-invalid" : ""}`}
                        value={values.display_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.display_name && errors.display_name && (
                        <small className="error">{errors.display_name}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="city">City:</label>
                      <input
                        type="text"
                        placeholder="City"
                        name="city"
                        className={`form-control${touched.city && errors.city ? " is-invalid" : ""}`}
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.city && errors.city && (
                        <small className="error">{errors.city}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="phone">Phone:</label>
                      <input
                        type="text"
                        placeholder="Phone Number"
                        name="phone"
                        maxLength={15}
                        className={`form-control${touched.phone && errors.phone ? " is-invalid" : ""}`}
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.phone && errors.phone && (
                        <small className="error">{errors.phone}</small>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Detail */}
              <div className="col-6">
                <h4 className="mb-4 col-12">Account Detail</h4>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="role_id">Account Type:</label>
                      <select
                        name="role_id"
                        className={`form-control${touched.role_id && errors.role_id ? " is-invalid" : ""}`}
                        value={values.role_id}
                        onChange={(evt) => {
                          handleChange(evt);
                          const options = getUserOptions();
                          const findRole = options.find((o) => o.role_id === evt.target.value);
                          setFieldValue("role_name", findRole?.name ?? "");
                          setFieldValue("member_max_exposure_amt", "");
                        }}
                        onBlur={handleBlur}
                      >
                        {getUserOptions().map((opt, idx) => (
                          <option key={idx} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {touched.role_id && errors.role_id && (
                        <small className="error">{errors.role_id}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="credit_reference">Credit Reference:</label>
                      <input
                        type="text"
                        placeholder="Credit Reference"
                        name="credit_reference"
                        className={`form-control${touched.credit_reference && errors.credit_reference ? " is-invalid" : ""}`}
                        value={values.credit_reference}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.credit_reference && errors.credit_reference && (
                        <small className="error">{errors.credit_reference}</small>
                      )}
                    </div>
                  </div>
                  {isUser && (
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="member_max_exposure_amt">Exposer Limit:</label>
                        <input
                          type="text"
                          placeholder="Exposer Limit"
                          name="member_max_exposure_amt"
                          className={`form-control${touched.member_max_exposure_amt && errors.member_max_exposure_amt ? " is-invalid" : ""}`}
                          value={values.member_max_exposure_amt}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.member_max_exposure_amt && errors.member_max_exposure_amt && (
                          <small className="error">{errors.member_max_exposure_amt}</small>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Commission Settings & Partnership & Min Max Bet */}
            <div className="row mt-4">
              <div className="col-12">
                <h4 className="mb-4 col-md-12">Commission Settings</h4>
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <td>Upline</td>
                      <td>{memberData?.commission_settings?.up_line ?? "0"}</td>
                    </tr>
                    <tr>
                      <td>Downline</td>
                      <td>
                        <input
                          type="text"
                          name="comm"
                          placeholder="0"
                          maxLength={4}
                          readOnly
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Our</td>
                      <td>{memberData?.commission_settings?.our ?? "0"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="col-12">
                <h4 className="mb-4 col-md-12">Partnership</h4>
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <td>Upline</td>
                      <td>{memberData?.partnership?.up_line ?? "0"}</td>
                    </tr>
                    <tr>
                      <td>Downline</td>
                      <td>
                        <div className="form-group">
                          <input
                            type="text"
                            name="spart1"
                            placeholder="0"
                            readOnly
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Our</td>
                      <td>{memberData?.partnership?.our ?? "0"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {isUser && (
                <div className="col-12">
                  <h4 className="mb-4 col-md-12">Min Max Bet</h4>
                  <table className="table table-striped table-bordered">
                    <tbody>
                      <tr>
                        <td rowSpan={2}>Min Bet</td>
                        <td>100</td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            placeholder="100"
                            name="member_min_bet"
                            value={values.member_min_bet}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td rowSpan={2}>Max Bet</td>
                        <td>5000000</td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            placeholder="5000000"
                            name="member_max_bet_amt"
                            value={values.member_max_bet_amt}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td rowSpan={2}>Delay</td>
                        <td>5.00</td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            placeholder="5.00"
                            name="bet_delay"
                            value={values.bet_delay}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Transaction Password */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="form-group col-3 float-right">
                  <label htmlFor="transaction_password">Transaction Password:</label>
                  <input
                    placeholder="Transaction Password"
                    type="password"
                    name="transaction_password"
                    className={`form-control${touched.transaction_password && errors.transaction_password ? " is-invalid" : ""}`}
                    value={values.transaction_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.transaction_password && errors.transaction_password && (
                    <small className="error">{errors.transaction_password}</small>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="row m-t-20">
              <div className="col-md-12">
                <div className="float-right">
                  <button type="submit" className="btn btn-submit">
                    Create Account
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
